import { useState } from 'react'
import { Flex } from '@radix-ui/themes'
import { MessageCallout } from '~/components/MessageCallout'
import type { StationSectionProps } from '../types'
import { defaultWindow, shiftWindow, windowForEpisode, type LeakV0Window } from './chartWindow'
import { LeakV0NotEnrolledNotice, LeakV0Panels } from './LeakV0Panels'
import { StationLeakV0ChartSection } from './StationLeakV0ChartSection'
import type { StationLeakV0Status } from './summary'
import { useLeakV0Episodes, type StationLeakV0Episode } from './useLeakV0Episodes'

interface StationLeakV0PanelProps extends StationSectionProps {
  status: StationLeakV0Status
}

// Stable identity, so a still-loading episodes query does not rebuild the chart model every render.
const NO_EPISODES: StationLeakV0Episode[] = []

const EnrolledPanel = ({ station, status }: StationLeakV0PanelProps) => {
  // Two windows: what was fetched, and the zoom viewport inside it. Reset drops the zoom only.
  const [fetched, setFetched] = useState(defaultWindow)
  const [zoom, setZoom] = useState<LeakV0Window | null>(null)
  const [selectedEpisodeId, setSelectedEpisodeId] = useState<string | null>(null)

  // Fetched from inside the expanded panel, never above it: the card unmounts this subtree when
  // collapsed, which is what keeps the ~2,000-bucket read off every station page load.
  const episodesQuery = useLeakV0Episodes(station.id)
  const episodes = episodesQuery.data ?? NO_EPISODES

  // One path for every window move, so a zoom viewport can never outlive the window it was taken
  // inside, and an arrow always clears the selection — which is what lets the same episode be
  // picked again to jump back to its onset.
  const showWindow = (next: LeakV0Window, episodeId: string | null) => {
    setFetched(next)
    setZoom(null)
    setSelectedEpisodeId(episodeId)
  }

  const selectEpisode = (episodeId: string) => {
    const episode = episodes.find((candidate) => candidate.id === episodeId)
    const next = episode === undefined ? null : windowForEpisode(episode.onsetAt)
    if (next !== null) showWindow(next, episodeId)
  }

  return (
    <Flex direction="column" gap="4">
      {episodesQuery.error ? (
        <MessageCallout
          color="amber"
          message={`Episodes unavailable · ${episodesQuery.error.message} — state bands, transition markers and the episode table are hidden`}
        />
      ) : null}

      <StationLeakV0ChartSection
        stationId={station.id}
        fetched={fetched}
        zoom={zoom}
        episodes={episodes}
        selectedEpisodeId={selectedEpisodeId}
        onShift={(direction) => showWindow(shiftWindow(fetched, direction), null)}
        onSelectEpisode={selectEpisode}
        onZoom={setZoom}
        onResetZoom={() => setZoom(null)}
      />

      <LeakV0Panels
        status={status}
        episodes={episodes}
        view={zoom ?? fetched}
        selectedEpisodeId={selectedEpisodeId}
      />
    </Flex>
  )
}

export const StationLeakV0Panel = (props: StationLeakV0PanelProps) =>
  props.status.enrolled ? <EnrolledPanel {...props} /> : <LeakV0NotEnrolledNotice />
